import * as React from 'react';
import { motion } from 'framer-motion';
import type { HTMLMotionProps, MotionStyle } from 'framer-motion';
import { cn } from '@/utils/cn';

/**
 * Props for the Button component.
 * Extends HTMLMotionProps to allow Framer Motion animation props.
 */
export interface ButtonProps extends HTMLMotionProps<'button'> {
  /**
   * The visual style of the button.
   */
  variant?:
    | 'white'
    | 'outline'
    | 'sidebar-default'
    | 'sidebar-active'
    | 'gray'
    | 'purple'
    | 'purple-dark'
    | 'purple-dark-light-border'
    | 'gray-gradient-border'
    | 'create-echo'
    | 'settings-modify'
    | 'settings-delete'
    | 'payments-download-all'
    | 'payments-download-row'
    | 'glass'
    | 'text-destructive'
    | 'save'
    | 'gray-variant'
    | 'sidebar-upgrade'
    | 'terms-accept'
    | 'terms-decline'
    | 'black'
    | 'pink'
    | 'white-pill'
    | 'black-pill';
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'auto';
  /**
   * If true, displays a loading spinner and disables the button.
   */
  isLoading?: boolean;
  /**
   * Custom width in pixels
   */
  width?: string | number | undefined;
  /**
   * Custom height in pixels
   */
  height?: string | number | undefined;
  /**
   * Custom border radius in pixels
   */
  borderRadius?: string | number | undefined;
  /**
   * Custom border width in pixels
   */
  borderWidth?: string | number | undefined;
  /**
   * Custom font size in pixels
   */
  fontSize?: string | number | undefined;
  /**
   * Custom font weight
   */
  fontWeight?: string | number | undefined;
  /**
   * Custom line height in pixels
   */
  lineHeight?: string | number | undefined;
  /**
   * Custom text color
   */
  textColor?: string | undefined;
}

type GradientBorderStyle = {
  border: string;
  backgroundClip: string;
  backgroundOrigin: string;
  backgroundImage: string;
  color?: string;
  borderRadius?: string;
};

function makeGradientBorder(
  borderSize: string,
  innerGradient: string,
  outerGradient: string,
  extras?: { color?: string; borderRadius?: string }
): GradientBorderStyle {
  return {
    border: `${borderSize} solid transparent`,
    backgroundClip: 'padding-box, border-box',
    backgroundOrigin: 'padding-box, border-box',
    backgroundImage: `${innerGradient}, ${outerGradient}`,
    ...extras,
  };
}

const VARIANT_STYLES: Partial<
  Record<NonNullable<ButtonProps['variant']>, GradientBorderStyle | Record<string, string>>
> = {
  'sidebar-default': makeGradientBorder(
    '3px',
    'linear-gradient(#F3F4F6, #F3F4F6)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)',
    { color: '#1A1036', borderRadius: '30px' }
  ),
  'sidebar-active': makeGradientBorder(
    '3px',
    'linear-gradient(90deg, #1A1036 0%, #2C1470 100%)',
    'linear-gradient(90deg, #45349E 0%, #BFB0F8 100%)',
    { color: '#FFFFFF', borderRadius: '30px' }
  ),
  purple: makeGradientBorder(
    '2px',
    'linear-gradient(90deg, var(--purple-500) 0%, var(--brand-800) 100%)',
    'linear-gradient(90deg, var(--brand-300) 0%, var(--purple-200) 100%)'
  ),
  'purple-dark': makeGradientBorder(
    '2px',
    'linear-gradient(90deg, rgba(162, 137, 255, 0.99) 0%, #1A1036 100%)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)'
  ),
  'purple-dark-light-border': makeGradientBorder(
    '2px',
    'linear-gradient(90deg, rgba(162, 137, 255, 0.99) 0%, #1A1036 100%)',
    'linear-gradient(90deg, #BFB0F8 0%, #E3DBFF 100%)'
  ),
  'gray-gradient-border': makeGradientBorder(
    '2px',
    'linear-gradient(#737373, #737373)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)'
  ),
  'create-echo': makeGradientBorder(
    '2px',
    'linear-gradient(0deg, #F3F4F6, #F3F4F6)',
    'linear-gradient(90deg, #BFB0F8 0%, #E3DBFF 100%)'
  ),
  'settings-modify': makeGradientBorder(
    '3px',
    'linear-gradient(0deg, #F3F4F6, #F3F4F6)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)'
  ),
  'payments-download-all': {
    backgroundClip: 'padding-box, border-box',
    backgroundOrigin: 'padding-box, border-box',
    backgroundImage:
      'linear-gradient(#1A1036, #1A1036), linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)',
  },
  'payments-download-row': {
    backgroundClip: 'padding-box, border-box',
    backgroundOrigin: 'padding-box, border-box',
    backgroundImage:
      'linear-gradient(#F3F4F6, #F3F4F6), linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)',
  },
  save: {
    background: 'linear-gradient(90deg, rgba(162, 137, 255, 0.99) 0%, #1A1036 100%)',
    border: 'none',
  },
  'sidebar-upgrade': makeGradientBorder(
    '3px',
    'linear-gradient(#FFFFFF, #FFFFFF)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)'
  ),
  'terms-accept': makeGradientBorder(
    '3px',
    'linear-gradient(#FFFFFF, #FFFFFF)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)'
  ),
  'terms-decline': makeGradientBorder(
    '3px',
    'linear-gradient(#EA9A9A, #EA9A9A)',
    'linear-gradient(90deg, #BFB0F8 0%, #1A1036 100%)'
  ),
};

function toPx(value: string | number): string {
  return typeof value === 'number' ? `${value}px` : value;
}

function applyCustomStyleProps(
  base: MotionStyle,
  props: Pick<
    ButtonProps,
    | 'width'
    | 'height'
    | 'borderRadius'
    | 'borderWidth'
    | 'fontSize'
    | 'fontWeight'
    | 'lineHeight'
    | 'textColor'
  >
): MotionStyle {
  return {
    ...base,
    ...(props.width && { width: toPx(props.width) }),
    ...(props.height && { height: toPx(props.height) }),
    ...(props.borderRadius && { borderRadius: toPx(props.borderRadius) }),
    ...(props.borderWidth && { borderWidth: toPx(props.borderWidth) }),
    ...(props.fontSize && { fontSize: toPx(props.fontSize) }),
    ...(props.fontWeight && { fontWeight: props.fontWeight }),
    ...(props.lineHeight && { lineHeight: toPx(props.lineHeight) }),
    ...(props.textColor && { color: props.textColor }),
  } as MotionStyle;
}

/**
 * Flexible button component with extensive variant system and custom style props.
 * Built with Framer Motion for smooth interactions and animations.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'white',
      size = 'default',
      style,
      isLoading = false,
      children,
      disabled,
      width,
      height,
      borderRadius,
      borderWidth,
      fontSize,
      fontWeight,
      lineHeight,
      textColor,
      ...props
    },
    ref
  ) => {
    const isSidebarDefault = variant === 'sidebar-default';
    const isSidebarActive = variant === 'sidebar-active';
    const isSidebar = isSidebarDefault || isSidebarActive;

    const isPurple = variant === 'purple';
    const isPurpleDark = variant === 'purple-dark';
    const isPurpleDarkLightBorder = variant === 'purple-dark-light-border';
    const isGrayGradientBorder = variant === 'gray-gradient-border';
    const isCreateEcho = variant === 'create-echo';
    const isSettingsModify = variant === 'settings-modify';
    const isSettingsDelete = variant === 'settings-delete';
    const isGlass = variant === 'glass';

    const isDownloadAll = variant === 'payments-download-all';
    const isDownloadRow = variant === 'payments-download-row';
    const isTextDestructive = variant === 'text-destructive';
    const isSave = variant === 'save';
    const isGrayVariant = variant === 'gray-variant';
    const isSidebarUpgrade = variant === 'sidebar-upgrade';

    const isTermsAccept = variant === 'terms-accept';
    const isTermsDecline = variant === 'terms-decline';

    const breathingAnimation = {
      animate: {
        boxShadow: [
          '0px 6px 25.3px 0px rgba(191, 176, 248, 0.45)',
          '0px 6px 30px 2px rgba(191, 176, 248, 0.6)',
          '0px 6px 25.3px 0px rgba(191, 176, 248, 0.45)',
        ],
      },
      transition: {
        duration: 2.5,
        ease: 'easeInOut',
        repeat: Infinity,
      },
    };

    const variantStyle = VARIANT_STYLES[variant];
    let customStyle: MotionStyle = {
      ...((style as MotionStyle) || {}),
      ...(variantStyle ? (variantStyle as unknown as MotionStyle) : {}),
    };

    customStyle = applyCustomStyleProps(customStyle, {
      width,
      height,
      borderRadius,
      borderWidth,
      fontSize,
      fontWeight,
      lineHeight,
      textColor,
    });

    return (
      <motion.button
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          !isSidebar && 'text-sm font-medium',
          {
            'bg-primary text-primary-foreground hover:bg-primary/90 shadow': variant === 'white',
            'hover:bg-accent hover:text-accent-foreground border border-input bg-background shadow-sm':
              variant === 'outline',
            'bg-transparent font-sans text-16 font-bold uppercase tracking-wide text-delete-red hover:text-red-400':
              isTextDestructive,
            'rounded-[30px] font-sans font-semibold text-white shadow-sm hover:brightness-110':
              isSave,
            'h-[49px] w-[154px] rounded-30 bg-gray-850 font-sans text-16 font-semibold leading-27 text-brand-800 hover:opacity-80':
              isGrayVariant,
            'transition-transform hover:scale-[1.02] active:scale-95': isSidebar,
            'font-sans text-18 font-extrabold leading-27 tracking-normal': isSidebar,
            'h-[49px] rounded-[30px] text-lg font-bold tracking-wide text-white shadow-[0_0_20px_rgba(162,137,255,0.4)] transition-opacity [--brand-300:theme(colors.brand.300)] [--brand-800:theme(colors.brand.800)] [--gray-100:theme(colors.gray.100)] [--purple-200:theme(colors.purple.200)] [--purple-500:theme(colors.purple.500)] hover:opacity-90':
              isPurple,
            'rounded-[30px] text-[16px] font-bold uppercase tracking-wide text-white hover:opacity-90':
              isPurpleDark || isPurpleDarkLightBorder || isGrayGradientBorder,
            'rounded-[30px] font-sans text-16 font-bold text-brand-800 shadow-sm hover:brightness-95':
              isTermsAccept,
            'rounded-[30px] font-sans text-16 font-bold text-brand-800 shadow-sm hover:brightness-110':
              isTermsDecline,
            'rounded-[30px] text-[14px] font-bold text-brand-800 transition-all hover:brightness-95':
              isCreateEcho,
            'rounded-[30px] text-[16px] font-semibold text-brand-800 hover:brightness-95':
              isSettingsModify,
            'rounded-[30px] border-[2px] border-settings-delete-border bg-transparent text-[16px] font-semibold text-settings-delete-text hover:bg-gray-100':
              isSettingsDelete,
            'rounded-[30px] border-[3px] border-transparent font-sans text-[16px] font-semibold leading-[27px] text-white hover:opacity-90':
              isDownloadAll,
            'rounded-[30px] border-[1.5px] border-transparent font-sans text-[14px] font-semibold leading-[21px] text-brand-800 hover:brightness-95':
              isDownloadRow,
            'relative inline-flex select-none items-center justify-center rounded-[24px] border border-white/50 bg-white/5 text-center align-middle font-sans text-sm font-medium text-white antialiased shadow-[inset_0_1px_0px_rgba(255,255,255,0.75),0_0_9px_rgba(0,0,0,0.2),0_3px_8px_rgba(0,0,0,0.15)] backdrop-blur-sm transition-all duration-300 before:pointer-events-none before:absolute before:inset-0 before:rounded-[24px] before:bg-gradient-to-br before:from-white/60 before:via-transparent before:to-transparent before:opacity-70 before:content-[""] after:pointer-events-none after:absolute after:inset-0 after:rounded-[24px] after:bg-gradient-to-tl after:from-white/30 after:via-transparent after:to-transparent after:opacity-50 after:content-[""] hover:bg-white/30':
              isGlass,
            'rounded-[5px] bg-gray-350 text-[12px] font-medium leading-[105%] text-brand-800 hover:bg-gray-350/90':
              variant === 'gray',
            'rounded-[5px] bg-brand-800 px-[5px] py-0 font-sans text-[10px] font-semibold leading-160 tracking-neg-2 text-white hover:bg-brand-800/90':
              variant === 'black',
            'rounded-[30px] bg-brand-300 font-sans text-[18px] font-extrabold leading-[27px] text-brand-800 hover:bg-brand-300/90':
              variant === 'pink',
            'rounded-[40px] bg-white px-[7px] py-0 font-sans text-[16px] font-medium leading-[12px] text-brand-800 hover:bg-gray-50':
              variant === 'white-pill',
            'rounded-[40px] bg-dark-400 px-[7px] py-0 font-sans text-[16px] font-medium leading-[12px] text-white hover:bg-dark-400/90':
              variant === 'black-pill',
            'relative h-[49px] w-full rounded-[30px] font-sans text-[18px] font-medium leading-[27px] text-brand-600 hover:brightness-95':
              isSidebarUpgrade,
          },
          !isSidebarDefault &&
            !isSidebarActive &&
            !isPurple &&
            !isPurpleDark &&
            !isPurpleDarkLightBorder &&
            !isGrayGradientBorder &&
            !isCreateEcho &&
            !isSettingsModify &&
            !isSettingsDelete &&
            !isGlass &&
            !isDownloadAll &&
            !isDownloadRow &&
            !isTextDestructive &&
            !isGrayVariant &&
            variant !== 'save' && {
              'h-10 px-4 py-2': size === 'default',
              'h-9 rounded-md px-3': size === 'sm',
              'h-11 px-8': size === 'lg',
              'h-10 w-10': size === 'icon',
            },
          isTextDestructive && {
            'h-auto px-0 py-0': size === 'auto',
          },
          (isTermsAccept || isTermsDecline) && {
            'h-[49px] w-[154px] px-0': true,
          },
          className
        )}
        ref={ref}
        style={customStyle}
        disabled={disabled || isLoading}
        {...(isPurple
          ? {
              whileTap: {
                scale: 0.98,
                backgroundImage: `
              linear-gradient(0deg, var(--brand-800), var(--brand-800)),
              linear-gradient(90deg, var(--purple-500) 0%, var(--brand-800) 100%)
            `,
                color: '#FFFFFF',
              },
            }
          : {})}
        {...(isPurpleDarkLightBorder ? breathingAnimation : {})}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className='mr-2 size-4 animate-spin'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </motion.button>
    );
  }
);
Button.displayName = 'Button';

export { Button };
